import LogsDefinitions from "../../logs/exports.js";
import {
    Automate,
    RootFilterBlock,
    FilterBlock,
    OutputBlock,
    BlockKind,
    Block,
    RegexRule,
} from "./types.js";

const STORAGE_KEY = "logsCord.automates";

const uid = () => `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const defaultLogType = (): string => LogsDefinitions[0]?.name || "";

export const isMessageLogType = (logType: string): boolean =>
    logType?.startsWith("message_");

export const createFilterBlock = (logType?: string): FilterBlock => ({
    id      : uid(),
    alias   : "block_" + Math.random().toString(36).slice(2, 6),
    kind    : "filter",
    config  : {
        logType : logType ?? defaultLogType(),
        windowMs: 5000,
        regexes : [],
    },
});

export const createRootBlock = (): RootFilterBlock => ({
    id      : uid(),
    alias   : "root",
    kind    : "root",
    config  : { logType: defaultLogType() },
});

export const createOutputBlock = (): OutputBlock => ({
    id      : uid(),
    kind    : "output",
    config  : {
        exportName  : "result",
        sourceAlias : "root",
        field       : "type",
        regexIndex  : 0,
        regexGroup  : 0,
    },
});

export const createDefaultAutomate = (): Automate => ({
    id      : uid(),
    name    : "Automate",
    blocks  : [
        createRootBlock(),
    ],
});

export function loadAutomates(): Automate[] {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw) {
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed) && parsed.length) {
                return parsed.map((auto) => normalizeAutomate(auto as Automate));
            }
        }
    } catch (err) {
        console.warn("Unable to read automates from localStorage", err);
    }

    const fallback = [createDefaultAutomate()];
    localStorage.setItem(STORAGE_KEY, JSON.stringify(fallback));
    return fallback;
}

export function persistAutomates(list: Automate[]) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
}

export function blockOutputsFor(block: Block | undefined): string[] {
    if (!block || block.kind === "output") return [];

    const base = ["executor", "target", "type", "createdAt", "extra"];
    if (block.kind === "filter" && isMessageLogType(block.config.logType))
        base.push("regexMatch");
    return base;
}

export function cloneRegex(rule?: Partial<RegexRule>): RegexRule {
    return {
        id      : uid(),
        pattern : rule?.pattern ?? "",
        flags   : rule?.flags ?? "",
    };
}

function normalizeAutomate(auto: Automate): Automate {
    if (!auto.blocks?.length) {
        return createDefaultAutomate();
    }

    const normalized = auto.blocks.map((b, idx) => {
        if (idx === 0) {
            const root = (b as RootFilterBlock);
            return {
                id      : root.id ?? createRootBlock().id,
                alias   : root.alias || "root",
                kind    : "root" as const,
                config  : {
                    logType: (root as any)?.config?.logType || defaultLogType(),
                },
            };
        }

        const block = b as Block;
        if (block.kind === "output") {
            return {
                id     : block.id ?? uid(),
                kind   : "output" as const,
                config : {
                    exportName  : (block as any)?.config?.exportName ?? "result",
                    sourceAlias : (block as any)?.config?.sourceAlias ?? "root",
                    field       : (block as any)?.config?.field ?? "type",
                    regexIndex  : (block as any)?.config?.regexIndex ?? 0,
                    regexGroup  : (block as any)?.config?.regexGroup ?? 0,
                },
            } as OutputBlock;
        }

        return {
            id      : block.id ?? createFilterBlock().id,
            alias   : block.alias || `block_${Math.random().toString(36).slice(2, 6)}`,
            kind    : "filter" as const,
            config  : {
                logType : (block as any)?.config?.logType || defaultLogType(),
                windowMs: (block as any)?.config?.windowMs,
                regexes : (block as any)?.config?.regexes ?? [],
            },
        } as FilterBlock;
    });

    return {
        ...auto,
        blocks: normalized,
    };
}
